import {
  createStyles,
  Divider,
  makeStyles,
  Theme,
  Typography,
} from "@material-ui/core";
import { blueGrey } from "@material-ui/core/colors";
import { useSnackbar } from "material-ui-snackbar-provider";
import React, { useCallback } from "react";

import { useOpen } from "../../../hooks/useOpen";

import ChangelogDialog from "./ChangelogDialog";
import TechnicalSupportDialog from "./TechnicalSupportDialog";

const useStyles = makeStyles<Theme>(({ breakpoints, spacing }) =>
  createStyles({
    root: {
      color: blueGrey[600],
      padding: spacing(1, 0),
      alignItems: "center",
      display: "flex",
      justifyContent: "space-between",
      minHeight: "fit-content",
      flexDirection: "row",
      [breakpoints.down("sm")]: {
        flexDirection: "column-reverse",
      },
    },
    links: {
      marginLeft: spacing(1),
      display: "flex",
      alignItems: "center",
      flexWrap: "wrap",
    },
    link: {
      color: "inherit",
      cursor: "pointer",
      textDecoration: "underline",
      border: "none",
      outline: "none",
      padding: 0,
    },
    divider: {
      height: spacing(2),
      backgroundColor: blueGrey[800],
      margin: spacing(0, 1),
    },
  })
);

interface Props {
  testId?: string;
}

const isBeta = /beta|localhost/.test(window.location.host);
const version = `Version ${process.env.REACT_APP_VERSION?.slice(0, -2)}${
  isBeta ? `-${process.env.REACT_APP_GIT_SHA?.substr(0, 7) ?? "local"}` : ""
}`;

const OSFooter: React.FunctionComponent<Props> = ({ testId }) => {
  const classes = useStyles();
  const snackbar = useSnackbar();
  const [changelogOpen, showChangelog, hideChangelog] = useOpen();
  const [
    technicalSupportOpen,
    showTechnicalSupport,
    hideTechnicalSupport,
  ] = useOpen();

  const handleCopyVersionToClipboard = useCallback(async () => {
    try {
      await navigator.clipboard?.writeText(version);
      snackbar.showMessage("Die Version wurde in die Zwischenablage kopiert");
    } catch (e) {}
  }, [snackbar]);

  return (
    <div className={classes.root} data-testid={`Footer-${testId}`}>
      <ChangelogDialog open={changelogOpen} onClose={hideChangelog} />
      <TechnicalSupportDialog
        open={technicalSupportOpen}
        onClose={hideTechnicalSupport}
      />
      <Typography variant="body2">
        <span onClick={isBeta ? handleCopyVersionToClipboard : undefined}>
          {version}
        </span>{" "}
        (
        <Typography
          className={classes.link}
          component="button"
          variant="body2"
          onClick={showChangelog}
        >
          Versionshinweise
        </Typography>
        )
      </Typography>
      <div className={classes.links}>
        <Typography variant="body2">© Verlag Dr. Otto Schmidt, Köln</Typography>
        <Divider className={classes.divider} orientation={"vertical"} />
        <Typography
          variant="body2"
          className={classes.link}
          component="a"
          href="https://www.otto-schmidt.de/impressum/"
          target="_blank"
        >
          Impressum
        </Typography>
        <Divider className={classes.divider} orientation={"vertical"} />
        <Typography
          variant="body2"
          className={classes.link}
          component="a"
          href="https://www.otto-schmidt.de/kontakt/"
          target="_blank"
        >
          Kontakt
        </Typography>
        <Divider className={classes.divider} orientation={"vertical"} />
        <Typography
          variant="body2"
          className={classes.link}
          component="a"
          href="https://www.otto-schmidt.de/datenschutz/"
          target="_blank"
        >
          Datenschutz
        </Typography>
        <Divider className={classes.divider} orientation={"vertical"} />
        <Typography
          variant="body2"
          className={classes.link}
          component="a"
          href="https://www.otto-schmidt.de/agb/"
          target="_blank"
        >
          AGB
        </Typography>
        <Divider className={classes.divider} orientation={"vertical"} />
        <Typography
          variant="body2"
          component="button"
          className={classes.link}
          onClick={showTechnicalSupport}
        >
          Technischer Support
        </Typography>
      </div>
    </div>
  );
};

export default OSFooter;
